import { useTranslation } from 'next-i18next'
import React from 'react'

import type { LogoBandId } from 'src/constants/logoBands'
import { LOGO_BANDS } from 'src/constants/logoBands'

type IProps = {
  /**
   * Which roster to show. Pick by the reader's dominant QUESTION, not by whether
   * the page is host-facing or renter-facing:
   *
   *   "Will you fill my space?"      → `demand`. A parking-lot owner is
   *                                    persuaded by fleet renters, not by other
   *                                    landlords. Most pillar host pages.
   *   "Am I in good company?"        → `supply` / `traditional`. A broker or a
   *                                    self-storage operator wants to see peers.
   *
   * So renter logos on a host page is correct and deliberate — pair it with a
   * `caption` that frames them as occupancy proof. Table: `docs/logo-bands.md`.
   */
  band: LogoBandId
  /**
   * How to frame the roster for THIS page's reader. Defaults to the band's own
   * caption; set it whenever the page asks a sharper question than "who works
   * with SpotMe" — see the surface table in `docs/logo-bands.md`.
   */
  caption?: LogoBandCaption
  className?: string
}

/**
 * Captions are chosen per SURFACE, not per band, because the same roster
 * answers different questions on different pages. Renter logos on
 * `/anfitrion/estacionamiento` are occupancy proof for a lot owner ("these
 * companies need parking"), while the same logos on the homepage are peer proof
 * for a renter ("these companies store here"). One caption per band would
 * mislabel the audience on half the surfaces.
 *
 * Literal keys, never `t(\`…${id}\`)` — a template literal doesn't type-check
 * against the typed resource map.
 */
const CAPTION_KEY = {
  supply: 'LOGO_BANDS.SUPPLY',
  supplyStorage: 'LOGO_BANDS.SUPPLY_STORAGE',
  supplyParking: 'LOGO_BANDS.SUPPLY_PARKING',
  demand: 'LOGO_BANDS.DEMAND',
  demandParking: 'LOGO_BANDS.DEMAND_PARKING',
  demandStorage: 'LOGO_BANDS.DEMAND_STORAGE',
  demandFleet: 'LOGO_BANDS.DEMAND_FLEET'
} as const

export type LogoBandCaption = keyof typeof CAPTION_KEY

/**
 * Scrolling band of real host / renter companies.
 *
 * Each mark is an alpha mask painted with `currentColor` rather than a picture,
 * so the strip is one tone at one optical weight whatever the source artwork
 * was, and a surface re-tints the whole band by setting a text colour. The
 * masks are pre-normalized by `yarn fetch-logos`; see `src/constants/logoBands.ts`
 * for the provenance rule.
 */
const Logos = ({ band, caption, className }: IProps) => {
  const { t } = useTranslation('common')
  const logos = LOGO_BANDS[band]
  const text = t(CAPTION_KEY[caption ?? band])

  // The marquee is a track duplicated once and translated by exactly -100%, so
  // the second copy sits where the first began and the loop is seamless. The
  // duplicate is decorative repetition, so it is hidden from assistive tech.
  //
  // Spacing lives on each item as trailing padding rather than as a `gap` on the
  // track: a gap would not apply BETWEEN the two tracks, so the spacing would
  // collapse every time the loop came around.
  //
  // Under reduced motion the track stops, which on a phone would strand the
  // reader on the first two logos forever. So the animation isn't merely
  // disabled there — the single track reflows into a centred wrapped grid and
  // the duplicate is dropped, which shows every company at once.
  const renderTrack = (ariaHidden: boolean) => (
    <ul
      aria-hidden={ariaHidden || undefined}
      className={`m-0 inline-flex w-max animate-slide-left list-none items-center p-0 align-middle group-hover:animation-pause motion-reduce:w-full motion-reduce:animate-none motion-reduce:flex-wrap motion-reduce:justify-center motion-reduce:gap-y-6 ${
        ariaHidden ? 'motion-reduce:hidden' : ''
      }`}
    >
      {logos.map((logo) => (
        <li
          key={logo.name}
          className="inline-flex shrink-0 items-center pe-10 motion-reduce:px-5 motion-reduce:pe-0 md:pe-14"
        >
          <span
            role={ariaHidden ? undefined : 'img'}
            aria-label={ariaHidden ? undefined : logo.name}
            className={`block bg-current mask-contain mask-center mask-no-repeat ${logo.className}`}
          />
        </li>
      ))}
    </ul>
  )

  return (
    <section
      aria-label={text}
      className={`w-full overflow-hidden py-2 text-ink-500/70 ${className ?? 'bg-white-500'}`}
    >
      {/* Editorial eyebrow, in ink rather than blue: it frames the band, it
          is not a link. `text-ink-200` clears AA on white and cream;
          the old `grey-600` sat at ~4.1:1. */}
      <p className="mb-6 px-5 text-center font-display text-[11px] tracking-[0.22em] text-ink-200 uppercase md:text-xs">
        {text}
      </p>
      <div className="group relative overflow-hidden mask-[linear-gradient(to_right,transparent_0,white_128px,white_calc(100%-128px),transparent_100%)] whitespace-nowrap motion-reduce:mask-none motion-reduce:whitespace-normal">
        {renderTrack(false)}
        {renderTrack(true)}
      </div>
    </section>
  )
}

export default React.memo(Logos)
